/**
 * User Details page with routing support
 */

import React from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Alert } from '@mui/material';
import { useQuery, useMutation } from '@tanstack/react-query';
import { teamApi } from '@/api/team';
import { useAuth } from '@/hooks/useAuth';
import { UserDetailView } from '@/components/user';

export const UserDetails: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user: currentUser } = useAuth();

  // Fetch user details
  // Include user key in query key to ensure proper data refresh on impersonation
  const {
    data: user,
    isLoading: isLoadingUser,
    error: userError,
  } = useQuery({
    queryKey: ['user-details', currentUser?.key, userId],
    queryFn: () => teamApi.getUserDetails(userId!),
    enabled: !!userId,
  });

  // Impersonation mutation
  const impersonationMutation = useMutation({
    mutationFn: teamApi.startImpersonation,
    onSuccess: () => {
      // Refresh the page to show impersonation status
      window.location.reload();
    },
    onError: (error: any) => {
      console.error('Failed to start impersonation:', error);
    },
  });

  const handleBack = () => {
    // Preserve all query params (search + filters) when navigating back to Users page
    const searchParams = new URLSearchParams(location.search);
    const query = searchParams.toString();
    const url = query ? `/users?${query}` : '/users';
    navigate(url);
  };

  const handleImpersonate = (userUuid: string) => {
    impersonationMutation.mutate({ user_uuid: userUuid });
  };

  if (isLoadingUser) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (userError || !user) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Failed to load user details. Please try again.
        </Alert>
      </Box>
    );
  }

  return (
    <UserDetailView
      user={user}
      onBack={handleBack}
      onImpersonate={handleImpersonate}
    />
  );
};
